import { log } from '@/log'
import { CartItem, CartItemList } from '@/models/cart/cart-item'
import {
  parseAddItemValidationError,
  parseUpdateItemCountValidationError,
} from '@/models/cart/error/error-response-parser'
import RemoveItemFromCartService from '@/models/cart/service/remove-item-from-cart-service'
import { RestoreRemovedItemFromCartService } from '@/models/cart/service/restore-removed-item-from-cart-service'
import { UpdateItemCountInCartService } from '@/models/cart/service/update-item-count-in-cart-service'
import { CookieService } from '@/services/cookie'
import { EventBusForThirdParty } from '@/usecases/event-bus-for-third-party/event-bus'
import { Either, left, right } from 'fp-ts/lib/Either'
import request, { ResponseError } from 'superagent'

export interface RemoveCartDeps {
  removeService: RemoveItemFromCartService,
}
export interface RemoveCartParam {
  id: string,
  wholeItems: CartItem[],
}

const createErrorMessage = (errors: Error[], parser: (response: request.Response) => string): string => {
  return errors.map((e: Error) => {
    try {
      const response = (e as ResponseError).response
      if (response) {
        return parser(response)
      }

      throw new Error('Response is not found.')
    } catch (_e) {
      return 'エラーが発生しました。※改善されない場合、お問い合わせよりカスタマーセンターまでお問い合わせください。'
    }
  }).join('<br>')
}

type RemoveCart = (deps: RemoveCartDeps) => (param: RemoveCartParam) => Promise<Either<Error, CartItem>>
const removeCart: RemoveCart = ({ removeService }) => async ({ id, wholeItems }) => {
  const cartItem = wholeItems.find(i => i.id === id)
  if (!cartItem) {
    return left(new Error(`Cart item ${id} is not found.`))
  }

  try {
    publishRemoveCartDeveloperReports(cartItem)
  } catch (e) {
    log.error(e)
  }

  const result = await removeService.remove(cartItem, { items: new CartItemList(wholeItems) })
  if (result.failed) {
    return left(new Error(
      result.errors.map(e => `[${e.name}] ${e.message}`).join('; ')
    ))
  }

  return right(cartItem)
}

/**
 * @internal
 * TODO: このメソッドはアイテム削除以外の処理なので、他のファイルに移動させる
 * @see https://github.com/Everforth/growthpalette-front/pull/7893#discussion_r1493323764
 */
function publishRemoveCartDeveloperReports(cartItem: CartItem) {
  EventBusForThirdParty.getInstance().publish('publish-developer-reports', [{
    userId: CookieService.getUserId(),
    level: 'info',
    timestamp: new Date(),
    /**
     * ユーザーからのお問い合わせのバグ調査のため、
     * 削除APIに送っているパラメータ、削除したVueコンポーネントで表示されているSKUの商品名と度数、ユーザーIdを送信する。
     * @see https://github.com/Everforth/growthpalette-front/issues/7848#issuecomment-1913937696
     */
    message: {
      cartItemId: cartItem.id,
      deletedSku: {
        name: cartItem.name,
        size: cartItem.size,
      },
      userId: CookieService.getUserId(),
    },
    reportSource: 'removeItemFromCart',
  }])
}

export interface RestoreCartDeps {
  restoreService: RestoreRemovedItemFromCartService,
}
export interface RestoreCartParam {
  id: string,
  wholeItems: CartItem[],
  removedItems: CartItem[],
}
type RestoreCart = (deps: RestoreCartDeps) => (param: RestoreCartParam) => Promise<Either<Error, CartItem>>
const restoreCart: RestoreCart = ({ restoreService }) => async ({ id, wholeItems, removedItems }) => {
  const cartItem = removedItems.find(i => i.id === id)
  if (!cartItem) {
    return left(new Error(`Cart item ${id} is not found in removedItems [${removedItems.map(i => i.id).join(', ')}]`))
  }

  try {
    publishRestoreCartDeveloperReports(cartItem)
  } catch (e) {
    log.error(e)
  }

  const result = await restoreService.restore(cartItem, { items: new CartItemList([...wholeItems, ...removedItems]) })
  if (result.failed) {
    return left(new Error(createErrorMessage(result.errors, parseAddItemValidationError)))
  }

  return right(cartItem)
}

export interface UpdateItemCountDeps {
  updateItemCountService: UpdateItemCountInCartService,
}
export interface UpdateItemCountParam {
  id: string,
  newCount: number,
  wholeItems: CartItem[],
}
type UpdateItemCount = (deps: UpdateItemCountDeps) => (param: UpdateItemCountParam) => Promise<Either<Error, CartItem>>

// TODO: The part of handling child product will be refactored and integrated into server-side or somewhere.
const updateItemCount: UpdateItemCount = ({ updateItemCountService }) => async ({ id, newCount, wholeItems }) => {
  const cartItem = wholeItems.find(i => i.id === id)
  if (!cartItem) {
    return left(new Error(`Cart item ${id} is not found.`))
  }

  const result = await updateItemCountService.updateCount(cartItem, newCount, { items: new CartItemList(wholeItems) })
  if (result.failed) {
    return left(new Error(createErrorMessage(result.errors, parseUpdateItemCountValidationError)))
  }

  return right(cartItem)
}

export const interaction = {
  removeCart,
  restoreCart,
  updateItemCount,
}

/**
 * @internal
 * TODO: このメソッドはリストア以外の処理なので、他のファイルに移動させる
 * @see https://github.com/Everforth/growthpalette-front/pull/7893#discussion_r1493323764
 */
function publishRestoreCartDeveloperReports(cartItem: CartItem) {
  EventBusForThirdParty.getInstance().publish('publish-developer-reports', [{
    userId: CookieService.getUserId(),
    level: 'info',
    timestamp: new Date(),
    /**
     * ユーザーからのお問い合わせのバグ調査のためにログを取る。
     * @see https://github.com/Everforth/growthpalette-front/pull/7893#discussion_r1493331191
     */
    message: {
      cartItemId: cartItem.id,
      restoredSku: {
        name: cartItem.name,
        fkuId: cartItem.fkuId,
        color: cartItem.color,
        skuId: cartItem.skuId,
        size: cartItem.size,
        unitCount: cartItem.unitCount,
      },
      userId: CookieService.getUserId(),
    },
    reportSource: 'restoreItemFromCart',
  }])
}
